import { useMedienpruefung, useSettings } from '../../hooks/queries'
import { groesse } from '../../lib/format'
import { PhotoIcon, VideoIcon, WelleIcon } from '../../components/Icons'

/**
 * Welche Dateien im Bestand nicht überall abspielbar sind (#41).
 *
 * **Warum es diese Karte gibt.** Aus dem Betrieb: „wie können wir feststellen,
 * welche Bilder / Videos aktuell noch nicht den richtigen Kriterien
 * entsprechen?" Seit dem Umrechnen beim Hochladen landet nichts
 * Unverträgliches mehr auf dem Server — was **vorher** hochgeladen wurde,
 * liegt aber weiter da, und die Datenbank sagt es nicht: `mime` nennt bei
 * einem Video den Behälter (`video/mp4`) und nicht die Kodierung. Ein
 * HEVC-Video steht dort wie ein H.264-Video.
 *
 * **Auf Knopfdruck.** Die Prüfung sieht auf dem Server in jede Datei hinein;
 * das ist keine Nebenrechnung, die beim Öffnen der Einstellungen von selbst
 * losläuft.
 *
 * **Und sie listet nur.** Umwandeln kann der Server nicht — kein ffmpeg, das
 * ist die Zielumgebung, und daran ändert diese Karte nichts. Was hier steht,
 * ist eine Arbeitsliste: den Beitrag neu schicken, dann geht die Datei durch
 * dasselbe Umrechnen wie jede andere. Deshalb steht die Reise daneben.
 */
export function MedienCard() {
  const einstellungen = useSettings()
  // Dieselbe Linie wie bei der Speicherkarte: ohne Decke ist dieses Konto
  // keine Verwaltung, und der Server hat das entschieden.
  const istVerwaltung = einstellungen.data?.ceiling !== undefined
  const pruefung = useMedienpruefung()

  if (!istVerwaltung) return null

  const daten = pruefung.data

  return (
    <div className="card card--seite">
      <h2 className="card__titel">Medien prüfen</h2>
      <p className="card__lead">
        Seit dem Umrechnen beim Hochladen landet nichts mehr auf dem Server,
        das nicht überall abspielt. Ältere Dateien können es trotzdem sein —
        und die Dateiart allein verrät es nicht: bei einem Video steht dort
        nur der Behälter, nicht die Kodierung.
      </p>

      <button
        type="button"
        className="button button--outline"
        style={{ width: 'auto' }}
        disabled={pruefung.isFetching}
        onClick={() => void pruefung.refetch()}
      >
        {pruefung.isFetching ? 'Wird geprüft …' : 'Bestand prüfen'}
      </button>

      {pruefung.isError && (
        <p role="alert" className="dienst__fehler">
          Die Prüfung ließ sich nicht durchführen.
        </p>
      )}

      {daten !== undefined && (
        <>
          <p className="card__lead" style={{ marginTop: 12 }}>
            {daten.befunde.length === 0
              ? `${daten.geprueft} Dateien geprüft — alle spielen überall ab.`
              : `${daten.geprueft} Dateien geprüft, ${daten.befunde.length} davon nicht überall abspielbar.`}
            {daten.offen > 0
              // **Ein abgeschnittener Lauf sagt das.** Sonst sähe eine halbe
              // Liste vollständig aus.
              && ` ${daten.offen} weitere stehen noch aus — beim nächsten Lauf.`}
          </p>

          {daten.befunde.length > 0 && (
            <ul className="medienliste">
              {daten.befunde.map((fund) => (
                <li key={fund.id} className="medienliste__zeile">
                  <span className="medienliste__zeichen" aria-hidden="true">
                    {fund.kind === 'video'
                      ? <VideoIcon size={18} />
                      : fund.kind === 'audio'
                        ? <WelleIcon size={18} />
                        : <PhotoIcon size={18} />}
                  </span>
                  <span className="medienliste__text">
                    <strong>{fund.befund}</strong>
                    <span className="medienliste__dazu">
                      {fund.trip_title} · {fund.mime}
                      {fund.codec !== '' && ` · ${fund.codec}`} · {groesse(fund.bytes)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}
